import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Copy, MessageSquare, RefreshCw, Search, Trash2 } from "lucide-react";
import { CenterPanel } from "@/components/layout/center-panel";
import { Button } from "@/components/ui/button";
import { KpiStrip } from "@/components/ui/kpi-strip";
import { Segmented } from "@/components/ui/segmented";
import { TaskActionButton } from "@/components/agent/task-action-button";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { chatApi, type DebugSession } from "@/api/chat";
import { formatDateTime, relativeTime } from "@/lib/task-display";
import { cn } from "@/lib/utils";

/**
 * Instance-admin only diagnostic page that lists EVERY chat session for an
 * agent — including:
 *   - sessions whose `channelId` no longer matches an existing channel
 *     (orphans — happens if a channel was deleted but its sessions weren't
 *     reassigned).
 *   - sessions with zero messages (empty shells, often left over from old
 *     bugs that double-fired the new-chat creation).
 *
 * Use it to find conversations that aren't appearing in the sidebar's
 * Recents under any channel, so we can recover or reassign them.
 *
 * Shown in the settings popup (Diagnostics) and at
 * /tenants/:tenantId/debug/sessions. Laid out like the popup's other list
 * pages: CenterPanel title, one toolbar row, a KPI strip, then grouped
 * bordered boxes like the Tasks list.
 */
type Filter = "all" | "with" | "empty" | "orphan";

const isOrphan = (s: DebugSession) => s.channelId !== null && s.channelName === null;

export function DebugSessions() {
  const { tenantId, isInstanceAdmin } = useTenant();
  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const [agentId, setAgentId] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");

  // Default to first agent
  const activeAgentId = agentId ?? agents[0]?.id ?? null;
  const activeAgent = agents.find((a) => a.id === activeAgentId);

  const {
    data: sessions = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ["debug-sessions", activeAgentId],
    queryFn: () => chatApi.debugSessions(activeAgentId!),
    enabled: !!activeAgentId && isInstanceAdmin,
  });

  // Bulk delete every empty session for the active agent.
  const queryClient = useQueryClient();
  const [confirmingDeleteEmpty, setConfirmingDeleteEmpty] = useState(false);
  const [lastDeleteCount, setLastDeleteCount] = useState<number | null>(null);
  const deleteEmpty = useMutation({
    mutationFn: () => chatApi.debugDeleteEmptySessions(activeAgentId!),
    onSuccess: (result) => {
      setLastDeleteCount(result.deleted);
      queryClient.invalidateQueries({ queryKey: ["debug-sessions", activeAgentId] });
      // Also refresh the sidebar's recents list.
      queryClient.invalidateQueries({ queryKey: ["chat-sessions", activeAgentId] });
    },
  });

  const totals = useMemo(() => {
    const total = sessions.length;
    const empty = sessions.filter((s) => s.messageCount === 0).length;
    const orphan = sessions.filter(isOrphan).length;
    return { total, empty, orphan, real: total - empty };
  }, [sessions]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sessions.filter((s) => {
      if (filter === "with" && s.messageCount === 0) return false;
      if (filter === "empty" && s.messageCount !== 0) return false;
      if (filter === "orphan" && !isOrphan(s)) return false;
      if (!q) return true;
      return [s.title, s.userEmail, s.channelName, s.id].some((v) => v?.toLowerCase().includes(q));
    });
  }, [sessions, filter, search]);

  // Group by channel (or "General" / orphaned channel)
  const groups = useMemo(() => {
    const buckets = new Map<string, { label: string; isOrphan: boolean; rows: DebugSession[] }>();
    for (const s of visible) {
      let key: string;
      let label: string;
      let orphan = false;
      if (s.channelId === null) {
        key = "__general__";
        label = "General (No Channel)";
      } else if (s.channelName === null) {
        key = `__orphan__${s.channelId}`;
        label = `Orphaned: channel ${s.channelId.slice(0, 8)} no longer exists`;
        orphan = true;
      } else {
        key = s.channelId;
        label = s.channelName;
      }
      const bucket = buckets.get(key) ?? { label, isOrphan: orphan, rows: [] };
      bucket.rows.push(s);
      buckets.set(key, bucket);
    }
    // Orphans first so they're impossible to miss, then by row count desc
    return Array.from(buckets.values()).sort((a, b) => {
      if (a.isOrphan !== b.isOrphan) return a.isOrphan ? -1 : 1;
      return b.rows.length - a.rows.length;
    });
  }, [visible]);

  if (!isInstanceAdmin) {
    return (
      <CenterPanel title="Sessions Debug">
        <div className="rounded-lg border border-border bg-surface p-8 text-center">
          <AlertTriangle size={28} className="mx-auto mb-3 text-text-muted" />
          <p className="text-sm text-text-secondary">This page is for Aztech staff only.</p>
        </div>
      </CenterPanel>
    );
  }

  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

  return (
    <CenterPanel title="Sessions Debug">
      <div className="space-y-4">
        <p className="-mt-3 text-sm text-text-muted">
          Every chat session for one agent, including empty ones and ones whose channel was
          deleted. Neither shows in the sidebar.
        </p>

        {/* Toolbar: agent, filter, search, then actions on the right */}
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={activeAgentId ?? ""}
            onChange={(e) => {
              setAgentId(e.target.value);
              setLastDeleteCount(null);
            }}
            disabled={agents.length === 0}
            aria-label="Agent"
            className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs"
          >
            {agents.length === 0 && <option value="">No agents</option>}
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>

          <Segmented<Filter>
            value={filter}
            onChange={setFilter}
            aria-label="Show"
            options={[
              { value: "all", label: `All ${totals.total}` },
              { value: "with", label: `With Messages ${totals.real}` },
              { value: "empty", label: `Empty ${totals.empty}` },
              { value: "orphan", label: `Orphaned ${totals.orphan}` },
            ]}
          />

          <div className="relative">
            <Search
              size={13}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search title, email or id"
              className="h-[30px] w-56 rounded-md border border-border bg-surface pl-8 pr-3 text-xs text-text-primary placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent"
            />
          </div>

          <div className="ml-auto flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => refetch()}
              disabled={!activeAgentId || isFetching}
            >
              <RefreshCw size={13} className={cn("mr-1", isFetching && "animate-spin")} />
              Refresh
            </Button>
            {totals.empty > 0 && (
              <Button
                size="sm"
                variant="destructive"
                disabled={deleteEmpty.isPending}
                onClick={() => setConfirmingDeleteEmpty(true)}
              >
                <Trash2 size={13} className="mr-1" />
                {deleteEmpty.isPending ? "Deleting…" : `Delete ${totals.empty} Empty`}
              </Button>
            )}
          </div>
        </div>

        {activeAgent && (
          <KpiStrip
            loading={isLoading}
            items={[
              { label: "Total Sessions", value: String(totals.total) },
              {
                label: "With Messages",
                value: String(totals.real),
                sub: totals.total ? `${Math.round((totals.real / totals.total) * 100)}% of all` : null,
              },
              {
                label: "Empty",
                value: String(totals.empty),
                sub: totals.empty ? "Not in the sidebar" : "None",
                subTone: totals.empty ? "warning" : "muted",
              },
              {
                label: "Orphaned",
                value: String(totals.orphan),
                sub: totals.orphan ? "Channel was deleted" : "None",
                subTone: totals.orphan ? "danger" : "muted",
              },
            ]}
          />
        )}

        {lastDeleteCount !== null && (
          <p className="text-xs text-emerald-500">
            {lastDeleteCount > 0
              ? `Deleted ${plural(lastDeleteCount, "empty session")}.`
              : "There were no empty sessions to delete."}
          </p>
        )}

        {isLoading && <p className="py-8 text-center text-sm text-text-muted">Loading sessions...</p>}
        {error && (
          <p className="rounded-lg border border-red-500/30 bg-red-500/5 px-4 py-3 text-sm text-red-500">
            Could not load sessions: {(error as Error).message}
          </p>
        )}

        {groups.map((group) => (
          <section key={group.label}>
            <p
              className={cn(
                "mb-1.5 flex items-center gap-1.5 px-1 text-[11px] font-semibold uppercase tracking-[0.06em]",
                group.isOrphan ? "text-red-500" : "text-text-muted",
              )}
            >
              {group.isOrphan && <AlertTriangle size={12} />}
              {group.label} <span className="font-normal opacity-70">· {group.rows.length}</span>
            </p>
            <div
              className={cn(
                "overflow-hidden rounded-lg border bg-surface",
                group.isOrphan ? "border-red-500/40" : "border-border",
              )}
            >
              <div className="grid grid-cols-[16px_minmax(0,1fr)_72px_96px_150px_96px] items-center gap-3 border-b border-border px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.05em] text-text-muted">
                <span />
                <span>Session</span>
                <span>Messages</span>
                <span>Last Message</span>
                <span>Created</span>
                <span className="text-right">Id</span>
              </div>
              {group.rows.map((s) => (
                <SessionRow key={s.id} session={s} />
              ))}
            </div>
          </section>
        ))}

        {!isLoading && !error && activeAgent && sessions.length === 0 && (
          <p className="py-8 text-center text-sm text-text-muted">
            No sessions exist for {activeAgent.name}.
          </p>
        )}
        {!isLoading && !error && sessions.length > 0 && visible.length === 0 && (
          <p className="py-8 text-center text-sm text-text-muted">No sessions match.</p>
        )}
      </div>

      {/* Confirm bulk-delete: same design as the sidebar's confirms */}
      {confirmingDeleteEmpty && activeAgent && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setConfirmingDeleteEmpty(false)}
        >
          <div
            className="w-[min(400px,calc(100vw-2rem))] rounded-lg border border-border bg-surface p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-2 text-sm font-semibold text-text-primary">
              Delete {plural(totals.empty, "empty session")}?
            </h3>
            <p className="mb-4 text-xs text-text-secondary">
              This permanently deletes every chat session for{" "}
              <span className="font-medium text-text-primary">{activeAgent.name}</span> that has
              no messages. Sessions with any messages are left alone. This cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              {/* Focus starts on Cancel, so pressing Enter deletes nothing. */}
              <Button size="sm" variant="ghost" autoFocus onClick={() => setConfirmingDeleteEmpty(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={deleteEmpty.isPending}
                onClick={() => {
                  setLastDeleteCount(null);
                  deleteEmpty.mutate();
                  setConfirmingDeleteEmpty(false);
                }}
              >
                Delete {totals.empty} Empty
              </Button>
            </div>
          </div>
        </div>
      )}
    </CenterPanel>
  );
}

function SessionRow({ session: s }: { session: DebugSession }) {
  const [copied, setCopied] = useState(false);
  const empty = s.messageCount === 0;
  const who = s.userEmail || (s.userId ? `User ${s.userId.slice(0, 8)}` : "System");

  return (
    <div className="grid grid-cols-[16px_minmax(0,1fr)_72px_96px_150px_96px] items-center gap-3 border-b border-border px-4 py-2.5 transition-colors last:border-0 hover:bg-surface-hover">
      <MessageSquare size={14} className={empty ? "text-text-muted" : "text-accent"} />

      <div className="min-w-0">
        <p className={cn("truncate text-sm font-medium", empty ? "text-text-muted" : "text-text-primary")}>
          {s.title}
        </p>
        <p className="truncate text-[11px] text-text-muted">{who}</p>
      </div>

      <span className={cn("text-xs tabular-nums", empty ? "text-amber-500" : "text-text-secondary")}>
        {empty ? "Empty" : s.messageCount}
      </span>

      <span
        className="text-xs text-text-muted"
        title={s.lastMessageAt ? formatDateTime(s.lastMessageAt) : undefined}
      >
        {s.lastMessageAt ? relativeTime(s.lastMessageAt) : "—"}
      </span>

      <span className="truncate text-xs text-text-muted">{formatDateTime(s.createdAt)}</span>

      <div className="flex items-center justify-end gap-1">
        <code className="text-[10px] text-text-muted">{s.id.slice(0, 8)}</code>
        <TaskActionButton
          label={copied ? "Copied" : "Copy full id"}
          onClick={() => {
            void navigator.clipboard.writeText(s.id).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            });
          }}
        >
          <Copy size={12} />
        </TaskActionButton>
      </div>
    </div>
  );
}
